import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ShoppingCart, CreditCard, Check, Sparkles, Shield, Clock } from "lucide-react";
import { skills } from "../data/skills";
import { notifyPurchaseConfirmation } from "../services/emailNotifications";
import Footer from "../components/Footer";

import { useTranslation } from 'react-i18next';
import SEOHead from "../components/SEOHead";
function slugify(name) {
  return name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

export default function CheckoutPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const skillId = searchParams.get("skillId");
  const skill = skills.find((s) => String(s.id) === skillId || slugify(s.name) === skillId);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    card: "",
    expiry: "",
    cvv: "",
  });
  const [purchased, setPurchased] = useState(false);
  const [purchasedSkill, setPurchasedSkill] = useState(null);
  const [errors, setErrors] = useState({});

  if (!skill) {
    return (
      <div className="min-h-screen bg-[#09090B] text-white flex items-center justify-center">
      <SEOHead
        title="Checkout | Max Employee"
        description="Complete your purchase securely. Skill checkout for Max OS Cloud agents."
        url="/checkout"
        noIndex={true}
      />
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-4">{t('checkout_page.skill_not_found')}</h1>
          <p className="text-zinc-400 mb-8">{t('checkout_page.the_skill_you_are_trying_to_purchase_was_not_found')}</p>
          <button
            onClick={() => navigate("/skills")}
            className="px-6 py-3 rounded-lg bg-amber-500 text-black font-medium hover:bg-amber-400 transition-colors"
          >
            Back to Marketplace
          </button>
        </div>
      <Footer />

      </div>
    );
  }

  const isFree = skill.price === 0;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = t('checkout_page.name_is_required');
    if (!formData.email.trim()) newErrors.email = t('checkout_page.email_is_required');
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = t('checkout_page.email_invalid');
    if (!isFree) {
      if (!formData.card.trim()) newErrors.card = t('checkout_page.card_is_required');
      if (!formData.expiry.trim()) newErrors.expiry = t('checkout_page.expiry_is_required');
      if (!formData.cvv.trim()) newErrors.cvv = t('checkout_page.cvv_is_required');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    // Save purchase to localStorage
    const purchases = JSON.parse(localStorage.getItem("maxemployee_purchases") || "[]");
    const purchase = {
      id: Date.now(),
      skillId: skill.id,
      skillName: skill.name,
      skillIcon: skill.icon,
      price: skill.price,
      buyerName: formData.name,
      buyerEmail: formData.email,
      purchasedAt: new Date().toISOString(),
      isFree,
    };
    purchases.push(purchase);
    localStorage.setItem("maxemployee_purchases", JSON.stringify(purchases));

    // Reset onboarding wizard so it shows after first purchase
    localStorage.removeItem("maxemployee.onboarding_wizard_completed");

    // Trigger email notification (mock)
    notifyPurchaseConfirmation(skill.name, formData.email);

    setPurchased(true);
    setPurchasedSkill({ ...skill, buyerName: formData.name, buyerEmail: formData.email });
  };

  // Confirmation screen
  if (purchased && purchasedSkill) {
    return (
      <div className="min-h-screen bg-[#09090B] text-white">
        <div className="max-w-2xl mx-auto px-6 lg:px-12 py-20">
          <div className="bg-zinc-950 border border-zinc-900 p-12 text-center">
            <div className="w-20 h-20 mx-auto mb-8 rounded-full bg-amber-500 flex items-center justify-center">
              <Check className="w-10 h-10 text-black" />
            </div>

            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-3">
              // COMPRA CONFIRMADA
            </div>
            <h1 className="font-display text-4xl uppercase mb-4">
              Thank you, {purchasedSkill.buyerName.split(" ")[0]}!
            </h1>
            <p className="text-zinc-400 max-w-md mx-auto leading-relaxed mb-8">
              Your purchase of skill <span className="text-amber-500 font-semibold">{purchasedSkill.name}</span> has been confirmed successfully.
              A confirmation email has been sent to <span className="text-white">{purchasedSkill.buyerEmail}</span>.
            </p>

            {/* Purchase summary */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 mb-8 text-left">
              <h3 className="font-mono text-xs uppercase tracking-wider text-zinc-500 mb-4">{t('checkout_page.purchase_summary')}</h3>
              <div className="flex items-center gap-4 mb-4">
                <div className="text-4xl">{purchasedSkill.icon}</div>
                <div>
              <div className="font-semibold text-lg">{purchasedSkill.name}</div>
              <div className="text-sm text-zinc-400">{purchasedSkill.category} • by {purchasedSkill.author}</div>
                </div>
              </div>
              <div className="flex items-center justify-between border-t border-zinc-800 pt-4">
                <span className="text-zinc-400">{t('checkout_page.total')}</span>
                <span className="font-display text-2xl text-amber-500">
                  {isFree ? "Free" : `$${purchasedSkill.price.toFixed(2)}`}
                </span>
              </div>
            </div>

            {/* Email notification badge */}
            <div className="flex items-center justify-center gap-2 text-sm text-zinc-500 mb-8">
              <Sparkles className="w-4 h-4 text-amber-500" />
              <span>Email notification sent (mock)</span>
            </div>

            <div className="flex justify-center gap-4 flex-wrap">
              <button
                onClick={() => navigate("/skills")}
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black px-6 py-3 font-bold uppercase tracking-wider text-sm transition-colors"
              >
                Continue Shopping
              </button>
              <button
                onClick={() => navigate("/")}
                className="inline-flex items-center gap-2 border border-zinc-800 hover:border-zinc-500 text-white px-6 py-3 font-bold uppercase tracking-wider text-sm transition-colors"
              >
                Go to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <div className="max-w-5xl mx-auto px-6 lg:px-12 py-8">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">{t('checkout_page.back')}</span>
        </button>

        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-2">
          // Checkout
        </div>
        <h1 className="font-display text-4xl uppercase mb-8">{t('checkout_page.complete_purchase')}</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Payment form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Personal info */}
              <div className="bg-zinc-950 border border-zinc-900 rounded-lg p-6">
                <h2 className="font-mono text-xs uppercase tracking-wider text-zinc-500 mb-6">{t('checkout_page.personal_info')}</h2>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1.5">{t('checkout_page.full_name')}</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder={t('checkout_page.your_full_name')}
                      className={`w-full bg-zinc-900 border ${errors.name ? "border-red-500" : "border-zinc-800"} rounded-lg px-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-amber-500 transition-colors`}
                    />
                    {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
                  </div>

                  <div>
                    <label className="block text-sm text-zinc-400 mb-1.5">{t('checkout_page.email')}</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      className={`w-full bg-zinc-900 border ${errors.email ? "border-red-500" : "border-zinc-800"} rounded-lg px-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-amber-500 transition-colors`}
                    />
                    {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
                  </div>
                </div>
              </div>

              {/* Payment info */}
              {!isFree && (
                <div className="bg-zinc-950 border border-zinc-900 rounded-lg p-6">
                  <div className="flex items-center gap-2 mb-6">
                    <CreditCard className="w-4 h-4 text-amber-500" />
                    <h2 className="font-mono text-xs uppercase tracking-wider text-zinc-500">Payment Info (mock)</h2>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm text-zinc-400 mb-1.5">{t('checkout_page.card_number')}</label>
                      <input
                        type="text"
                        name="card"
                        value={formData.card}
                        onChange={handleChange}
                        placeholder="0000 0000 0000 0000"
                        maxLength={19}
                        className={`w-full bg-zinc-900 border ${errors.card ? "border-red-500" : "border-zinc-800"} rounded-lg px-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-amber-500 transition-colors font-mono`}
                      />
                      {errors.card && <p className="text-red-400 text-xs mt-1">{errors.card}</p>}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-zinc-400 mb-1.5">{t('checkout_page.expiry')}</label>
                        <input
                          type="text"
                          name="expiry"
                          value={formData.expiry}
                          onChange={handleChange}
                          placeholder={t('checkout_page.mmaa')}
                          maxLength={5}
                          className={`w-full bg-zinc-900 border ${errors.expiry ? "border-red-500" : "border-zinc-800"} rounded-lg px-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-amber-500 transition-colors font-mono`}
                        />
                        {errors.expiry && <p className="text-red-400 text-xs mt-1">{errors.expiry}</p>}
                      </div>
                      <div>
                        <label className="block text-sm text-zinc-400 mb-1.5">{t('checkout_page.cvv')}</label>
                        <input
                          type="text"
                          name="cvv"
                          value={formData.cvv}
                          onChange={handleChange}
                          placeholder="000"
                          maxLength={4}
                          className={`w-full bg-zinc-900 border ${errors.cvv ? "border-red-500" : "border-zinc-800"} rounded-lg px-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-amber-500 transition-colors font-mono`}
                        />
                        {errors.cvv && <p className="text-red-400 text-xs mt-1">{errors.cvv}</p>}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mt-4 text-xs text-zinc-600">
                    <Shield className="w-3.5 h-3.5" />
                    <span>Simulated payment — no real data is processed</span>
                  </div>
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-black px-6 py-4 font-bold uppercase tracking-wider transition-colors rounded-lg text-sm"
              >
                <ShoppingCart className="w-4 h-4" />
                {isFree ? "Add to Agent" : `Confirm Purchase — $${skill.price.toFixed(2)}`}
              </button>
            </form>
          </div>

          {/* Order summary sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-8 bg-zinc-950 border border-zinc-900 p-6 rounded-lg">
              <h3 className="font-mono text-xs uppercase tracking-wider text-zinc-500 mb-6">{t('checkout_page.order_summary')}</h3>

              <div className="flex items-center gap-4 mb-6">
                <div className="text-4xl">{skill.icon}</div>
                <div>
                  <div className="font-semibold text-lg">{skill.name}</div>
                  <div className="text-sm text-zinc-400">{skill.category}</div>
                  <div className="text-sm text-zinc-500">by {skill.author}</div>
                </div>
              </div>

              <div className="space-y-3 border-t border-zinc-900 pt-4 mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-500">{t('checkout_page.subtotal')}</span>
                  <span className="text-zinc-300">{isFree ? "Free" : `$${skill.price.toFixed(2)}`}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-500">{t('checkout_page.discount')}</span>
                  <span className="text-emerald-400">$0.00</span>
                </div>
              </div>

              <div className="flex items-center justify-between border-t border-zinc-900 pt-4 mb-6">
                <span className="font-semibold">{t('checkout_page.total')}</span>
                <span className="font-display text-2xl text-amber-500">
                  {isFree ? "Free" : `$${skill.price.toFixed(2)}`}
                </span>
              </div>

              {/* Trial badge */}
              {skill.isTrial && !isFree && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs mb-4">
                  <Clock className="w-3.5 h-3.5" />
                  <span>{skill.trialDays}-day trial included</span>
                </div>
              )}

              {/* Features */}
              <div className="space-y-2 text-xs text-zinc-500">
                <div className="flex items-center gap-2">
                  <Check className="w-3.5 h-3.5 text-emerald-400" />
                  <span>{t('checkout_page.immediate_access_after_confirmation')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-3.5 h-3.5 text-emerald-400" />
                  <span>{t('checkout_page.future_updates_included')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-3.5 h-3.5 text-emerald-400" />
                  <span>{t('checkout_page.chat_support')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
